import {forwardRef, FC, useEffect, useRef, PropsWithChildren, CSSProperties, useState, MutableRefObject} from 'react';
import ReactDOM from 'react-dom';
import styles from './styles/BaseModalWindow.module.scss';
import {Button} from '../buttons/button';
import classNames from 'classnames';
import {CrossButton, ExpandButton, TurnButton} from '../buttons/iconButtons/other/PanelCollection';
import {Dropdown} from 'antd';
import {useTabsContext} from '../../system/providers/tabsProvider';
import {ReactPortal} from './ReactPortal';
import * as QuickAccessButtons from '../buttons/iconButtons/special/QuickAccessBarCollection';
import { useOptionalTourModalEducation } from '../tours/TourModal/TourModalContext';
import Tooltip from '../tooltips/Tooltip';
import { ModalKey } from '../../libs/ui-settings-manager/interfaces';
import { UISettingsManager } from '../../libs/ui-settings-manager/UISettingsManager';

interface IOverlayProps {
  style?: CSSProperties;
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
}

const Overlay: FC<IOverlayProps> = (props) => {
  return (
    <div
      className={styles.modalOverlay}
      style={props.style}
      onKeyDown={(e) => props.onKeyDown && props.onKeyDown(e)}
    >
      {props.children}
    </div>
  );
};

interface IModalWindowProps {
  className?: string;
  style?: CSSProperties;
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  baseStyle: string;
}
export const ModalWindow = forwardRef<HTMLDivElement, PropsWithChildren<IModalWindowProps>>((props, ref) => {
  return (
    <Overlay style={props.style} onKeyDown={(e) => props.onKeyDown && props.onKeyDown(e)}>
      <div tabIndex={-1} ref={ref} className={classNames(props.baseStyle, props.className)}>
        {props.children}
      </div>
    </Overlay>
  );
});

export interface IButton {
  onClick: () => void;
  title?: string;
  disabled?: boolean;
  sendRequestSpinner?: boolean;
  sendRequestGlobal?: boolean;
  hidden?: boolean;
  tabIndex?: number;
  'data-field-id'?: string;
}

declare type OverlayFunc = () => React.ReactElement;

export interface IModalDropDownButton extends IButton {
  overlay: React.ReactElement | OverlayFunc;
}

interface IObjectProps {
  header: string;
  ok: IButton;
  cancel: IButton;
  modalWindowClassName?: string;
  style?: CSSProperties;
  primary?: boolean;
  footerStyle?: string;
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  otherAction?: IButton;
  fullScreen?: boolean;
  dropDownButton?: IModalDropDownButton;
  autoFocus?: boolean;
  autoFocusForce?: boolean;
  className?:string
  modalTourId?: string;
  okButtonTourRef?: MutableRefObject<HTMLButtonElement | null>;
  modalKey?: ModalKey;
}
export const BaseModalWindow: FC<IObjectProps> = (props) => {
  const okButtonRef = useRef<HTMLButtonElement | null>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const otherActionButtonRef = useRef<HTMLButtonElement>(null);
  const modalWindowRef = useRef<HTMLDivElement>(null);
  const [fullScreen, setFullScreen] = useState<boolean>(() => {
        if (!props.modalKey) return false;
        return UISettingsManager.getModalSize(props.modalKey) === 'fullSize';
    });
  const [autoFocus, setAutoFocus] = useState(props.autoFocus ?? true);
  const [autoFocusForce, setautoFocusForce] = useState(props.autoFocusForce ?? true);
  const tourModalEducation = useOptionalTourModalEducation();
  const isModalTourAvailable = Boolean(tourModalEducation?.isModalTourAvailable(props.modalTourId));

  const setOkButtonRef = (element: HTMLButtonElement | null) => {
    okButtonRef.current = element;

    if (props.okButtonTourRef) {
      props.okButtonTourRef.current = element;
    }
  };

  const renderModalTourButton = () => {
    if (!isModalTourAvailable || !tourModalEducation) return null;

    return (
      <Tooltip title="Пройти обучение">
        <QuickAccessButtons.TourEducationButton
          sizeVariant="mini"
          colorVariant="default"
          onClick={() => tourModalEducation.startModalTour(props.modalTourId)}
        />
      </Tooltip>
    );
  };

  useEffect(() => {
    setAutoFocus(props?.autoFocus ?? true);
    setautoFocusForce(props.autoFocusForce ?? true);
  }, [props.autoFocus, props.autoFocusForce]);

  useEffect(() => {
    let mw = modalWindowRef.current as HTMLDivElement;
    if (autoFocus && mw) {
      mw.focus();
      mw.onkeydown = (e) => {
        switch (e.key) {
          case 'Enter': {
            okButtonRef.current?.click();
            break;
          }
          case 'Escape': {
            cancelButtonRef.current?.click();
            break;
          }
        }
      };
    }
  }, [autoFocus, autoFocusForce]);

  useEffect(() => {
    if (!props.modalKey) return;
    const handler = () => {
      const size = UISettingsManager.getModalSize(props.modalKey!);
      setFullScreen(size === 'fullSize');
    };
    window.addEventListener('uiSettingsChanged', handler);
    return () => window.removeEventListener('uiSettingsChanged', handler);
  }, [props.modalKey]);

  // Обёртка над setFullScreen, которая ещё и сохраняет в localStorage
  const handleSetFullScreen = (value: boolean) => {
    setFullScreen(value);
    if (props.modalKey) {
      UISettingsManager.setModalSize(
        props.modalKey,
        value ? 'fullSize' : 'compactSize'
      );
    }
  };

  return <>

    <ReactPortal wrapperId="react-portal-modal-container">
      
      <ModalWindow
        baseStyle={fullScreen ? styles.fullScreenModal : styles.modalEditValues}
        ref={modalWindowRef}
        className={props.modalWindowClassName}
        style={props.style}
        onKeyDown={(e) => props.onKeyDown && props.onKeyDown(e)}
      >
        <header>
          <div className={styles.headerContainer}>
            {props.fullScreen ? (
              <>
                <span className={styles.title}>{props.header}</span>
                <div className={styles.fullScreen}>
                  {fullScreen ? (
                    <TurnButton
                      sizeVariant="mini"
                      onClick={() => handleSetFullScreen(false)}
                    />
                  ) : (
                    <ExpandButton
                      sizeVariant="mini"
                      onClick={() => handleSetFullScreen(true)}
                    />
                  )}
                  {renderModalTourButton()}
                  <CrossButton sizeVariant="mini" onClick={() => props.cancel.onClick()} />
                </div>
              </>
            ) : (
              <>
                <span className={styles.title}>{props.header}</span>
                {renderModalTourButton()}
                <button className={styles.closeButton} onClick={() => props.cancel.onClick()}></button>
              </>
            )}
          </div>
        </header>
        <main className={styles.main}>
          <div className={styles.mainContainer}>{props.children}</div>
        </main>
        <footer>
          <div className={classNames(styles.footer, props.className)}>
            {!props.cancel.hidden && (
              <div className={styles.footerButton}>
                <Button
                  ref={cancelButtonRef}
                  variant={props.primary ? 'primary' : 'danger'}
                  onClick={() => {
                    props.cancel.onClick();
                  }}
                >
                  {props.cancel?.title ?? 'Отмена'}
                </Button>
              </div>
            )}
            {!props.ok?.hidden && (
              <div className={styles.footerButton}>
                <Button
                  ref={setOkButtonRef}
                  variant="primary"
                  disabled={props.ok?.disabled}
                  onClick={() => {
                    props?.ok.onClick();
                  }}
                  isLoadingSpinner={props.ok?.sendRequestSpinner}
                >
                  {props.ok?.title ?? 'OK'}
                </Button>
              </div>
            )}
              {props.otherAction && (
                <Button
                  ref={otherActionButtonRef}
                  variant="outsecondary"
                  disabled={props.otherAction?.disabled}
                  onClick={() => {
                    props?.otherAction?.onClick?.();
                  }}
                >
                  {props.otherAction?.title}
                </Button>
              )}
              {props.dropDownButton && (
                <Dropdown
                  overlay={props.dropDownButton?.overlay}
                  trigger={['click']}
                  disabled={props.dropDownButton?.disabled}
                >
                  <Button
                    className={styles.button}
                    onClick={() => {}}
                    disabled={props.dropDownButton?.disabled}
                    variant="primary"
                  >
                    {props.dropDownButton?.title}
                  </Button>
                </Dropdown>
              )}
          </div>
        </footer>
      </ModalWindow>
    </ReactPortal>
    </>
};
